import { lazy, Suspense } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import ModernBrowserInterstitial from 'app/components/interstitials/ModernBrowserInterstitial';
import CompanyTypeRestrictor from 'app/components/providers/CompanyTypeRestrictor';
import AuthRoute from 'app/components/shared/AuthRoute';
import { payorRoutes } from 'app/constants/routes';
import { CompanyTypeId } from 'app/constants/user';
import LoadingScreen from 'app/pages/shared/LoadingScreen';
import { retry } from 'app/utils/lib/file';

import PayorDataLoader from '../../Layouts/PayorDataLoader';
import PayorRoutesLayout from '../../Layouts/PayorRoutesLayout';

// Lazy-loaded route components
const LazyBillingReportRoute = lazy(() => retry(() => import('app/pages/payor/Reports/BillingReport')));
const LazyCreditLimitReportRoute = lazy(() => retry(() => import('app/pages/payor/Reports/CreditLimitReport')));
const LazyCreditRoute = lazy(() => retry(() => import('app/pages/payor/Credit')));
const LazyCreditsRoute = lazy(() => retry(() => import('app/pages/payor/Credits')));
const LazyDashboardRoute = lazy(() => retry(() => import('app/pages/payor/Dashboard')));
const LazyFourOhFour = lazy(() => retry(() => import('app/pages/error/FourOhFour')));
const LazyHighwayAlertsRoute = lazy(() => retry(() => import('app/pages/payor/Reports/HighwayAlerts')));
const LazyHoldsReportRoute = lazy(() => retry(() => import('app/pages/payor/Reports/HoldsReport')));
const LazyInvoiceRefundsReportRoute = lazy(() => retry(() => import('app/pages/payor/Reports/InvoiceRefundsReport')));
const LazyInvoiceRoute = lazy(() => retry(() => import('app/pages/payor/Invoice')));
const LazyInvoicesRoute = lazy(() => retry(() => import('app/pages/payor/Invoices')));
const LazyLoadRoute = lazy(() => retry(() => import('app/pages/payor/Load')));
const LazyLoadsRoute = lazy(() => retry(() => import('app/pages/payor/Loads')));
const LazyPaymentRoute = lazy(() => retry(() => import('app/pages/payor/Payment')));
const LazyPaymentsRoute = lazy(() => retry(() => import('app/pages/payor/Payments')));
const LazyPayorInvoiceReviewRoute = lazy(() => retry(() => import('app/pages/payor/Reports/PayorInvoiceReview')));
const LazyPreviousSettlmentsRoute = lazy(() => retry(() => import('app/pages/payor/Reports/PreviousSettlements')));
const LazyGotpFeesRoute = lazy(() => retry(() => import('app/pages/payor/Reports/GotpFees')));
// eslint-disable-next-line @stylistic/max-len
const LazyPreviousWeeklySettlmentsRoute = lazy(() => retry(() => import('app/pages/payor/Reports/PreviousWeeklySettlements')));
const LazyProfileRoute = lazy(() => retry(() => import('app/pages/payor/Profile')));
const LazyRelationshipRoute = lazy(() => retry(() => import('app/pages/payor/Relationship')));
const LazyRelationshipsRoute = lazy(() => retry(() => import('app/pages/payor/Relationships')));
const LazyRevenueShareroute = lazy(() => retry(() => import('app/pages/payor/Reports/RevenueShare')));
const LazySettlementEditRoute = lazy(() => retry(() => import('app/pages/payor/SettlementEdit')));
const LazySettlementRoute = lazy(() => retry(() => import('app/pages/payor/Settlement')));
const LazySettlementSuccessRoute = lazy(() => retry(() => import('app/pages/payor/SettlementSuccess')));
const LazySettlementsAcceptTermsRoute = lazy(() => retry(() => import('app/pages/payor/SettlementsAcceptTerms')));
const LazySettlementsRoute = lazy(() => retry(() => import('app/pages/payor/Settlements')));
const LazyUserCreateRoute = lazy(() => retry(() => import('app/pages/payor/UserCreate')));
const LazyUserEditRoute = lazy(() => retry(() => import('app/pages/payor/UserEdit')));
const LazyUnreconciledFundsReportRoute = lazy(() => retry(() => import('app/pages/payor/Reports/UnreconciledFunds')));

// Component wrapper types
type ComponentProps = Record<string, unknown>;

// DefaultRedirect component
const defaultPath = `/payor/${payorRoutes.dashboard}`;
export const DefaultRedirect = () => <Navigate to={defaultPath} />;

// Wrapper components for lazy-loaded routes
export const BillingReport = (props: ComponentProps) => <LazyBillingReportRoute {...props} />;
export const Credit = (props: ComponentProps) => <LazyCreditRoute {...props} />;
export const CreditLimitReport = (props: ComponentProps) => <LazyCreditLimitReportRoute {...props} />;
export const Credits = (props: ComponentProps) => <LazyCreditsRoute {...props} />;
export const Dashboard = (props: ComponentProps) => <LazyDashboardRoute {...props} />;
export const FourOhFour = (props: ComponentProps) => <LazyFourOhFour {...props} />;
export const HighwayAlerts = (props: ComponentProps) => <LazyHighwayAlertsRoute {...props} />;
export const HoldsReport = (props: ComponentProps) => <LazyHoldsReportRoute {...props} />;
export const UnreconciledFundsReport = (props: ComponentProps) => <LazyUnreconciledFundsReportRoute {...props} />;
export const Invoice = (props: ComponentProps) => <LazyInvoiceRoute {...props} />;
export const InvoiceRefundsReport = (props: ComponentProps) => <LazyInvoiceRefundsReportRoute {...props} />;
export const Invoices = (props: ComponentProps) => <LazyInvoicesRoute {...props} />;
export const Load = (props: ComponentProps) => <LazyLoadRoute {...props} />;
export const Loads = (props: ComponentProps) => <LazyLoadsRoute {...props} />;
export const Payment = (props: ComponentProps) => <LazyPaymentRoute {...props} />;
export const Payments = (props: ComponentProps) => <LazyPaymentsRoute {...props} />;
export const PayorInvoiceReview = (props: ComponentProps) => <LazyPayorInvoiceReviewRoute {...props} />;
export const PreviousSettlements = (props: ComponentProps) => <LazyPreviousSettlmentsRoute {...props} />;
export const PreviousWeeklySettlements = (props: ComponentProps) => <LazyPreviousWeeklySettlmentsRoute {...props} />;
export const GotpFees = (props: ComponentProps) => <LazyGotpFeesRoute {...props} />;
export const Profile = (props: ComponentProps) => <LazyProfileRoute {...props} />;
export const Relationship = (props: ComponentProps) => <LazyRelationshipRoute {...props} />;
export const Relationships = (props: ComponentProps) => <LazyRelationshipsRoute {...props} />;
export const RevenueShare = (props: ComponentProps) => <LazyRevenueShareroute {...props} />;
export const Settlement = (props: ComponentProps) => <LazySettlementRoute {...props} />;
export const SettlementEdit = (props: ComponentProps) => <LazySettlementEditRoute {...props} />;
export const SettlementSuccess = (props: ComponentProps) => <LazySettlementSuccessRoute {...props} />;
export const Settlements = (props: ComponentProps) => <LazySettlementsRoute {...props} />;
export const SettlementsAcceptTerms = (props: ComponentProps) => <LazySettlementsAcceptTermsRoute {...props} />;
export const UserCreate = (props: ComponentProps) => <LazyUserCreateRoute {...props} />;
export const UserEdit = (props: ComponentProps) => <LazyUserEditRoute {...props} />;

// Layout component
export const PayorLayout = () => {
  return (
    <AuthRoute>
      <CompanyTypeRestrictor permittedCompanyTypeIds={[CompanyTypeId.Payor]}>
        <PayorDataLoader>
          <PayorRoutesLayout
            body={(
              <Suspense fallback={<LoadingScreen />}>
                <Outlet />
              </Suspense>
            )}
          />

          <ModernBrowserInterstitial />
        </PayorDataLoader>
      </CompanyTypeRestrictor>
    </AuthRoute>
  );
};
